import { SETTINGS_ROUTES, INVOICE_SERIES_ROUTE, PRODUCT_ROUTES, PROFILE_ROUTE } from './settingsRoutes';

/**
 * Breadcrumb kaynağı: route yolu → i18n etiket anahtarı.
 *
 * Sidebar'ın `navGroups`'undan türetilmiyor çünkü breadcrumb, sidebar'da hiç
 * görünmeyen ara ve detay sayfalarını da adlandırmak zorunda (ürün detayı,
 * ayarların alt sayfaları, yazdırma ekranları). Tek bir düz harita tutmak, iki
 * kaynağı senkron tutmaya çalışmaktan daha az kırılgan.
 *
 * Eşleşme prefix tabanlıdır: bir yol için eşleşen TÜM girdiler, en kısadan en
 * uzuna zincir olur. Bu yüzden burada bir kayıt olmayan ara segment (örn.
 * `integrations`) breadcrumb'da kendiliğinden atlanır.
 */
export const ROUTE_LABELS: Readonly<Record<string, string>> = {
  '/dashboard': 'nav.dashboard',

  // Katalog
  '/dashboard/products': 'nav.products',
  [PRODUCT_ROUTES.matching]: 'nav.productMatching',
  '/dashboard/categories': 'nav.categories',
  '/dashboard/brands': 'nav.brands',
  '/dashboard/attributes': 'nav.attributes',
  '/dashboard/stock-movements': 'nav.stockMovements',
  '/dashboard/media': 'nav.media',
  '/dashboard/imports': 'nav.imports',

  // Stok
  '/dashboard/warehouses': 'nav.warehouses',
  '/dashboard/stock-receipts': 'nav.stockReceipts',

  // Satış
  '/dashboard/quotes': 'nav.quotes',
  '/dashboard/orders': 'nav.orders',
  '/dashboard/unresolved-order-items': 'nav.unresolvedOrderItems',
  '/dashboard/invoices': 'nav.invoices',
  '/dashboard/inbound-invoices': 'nav.inboundInvoices',
  [INVOICE_SERIES_ROUTE]: 'nav.invoiceSeries',
  '/dashboard/delivery-notes': 'nav.deliveryNotes',

  // Finans
  '/dashboard/reports': 'nav.reports',
  '/dashboard/accounts': 'nav.currentManagement',
  '/dashboard/cash-bank': 'nav.cashBank',
  '/dashboard/expenses': 'nav.expenses',
  '/dashboard/price-lists': 'nav.priceLists',
  '/dashboard/check-notes': 'nav.checkNotes',

  // Operasyon
  '/dashboard/marketplace-sync': 'nav.marketplaceSync',

  // Hesap
  [PROFILE_ROUTE]: 'profile.page.title',

  // Ayarlar
  [SETTINGS_ROUTES.root]: 'settings.layout.title',
  [SETTINGS_ROUTES.localization]: 'settings.rail.localization',
  [SETTINGS_ROUTES.operationsDefaults]: 'settings.rail.operationsDefaults',
  [SETTINGS_ROUTES.fx]: 'settings.rail.fx',
  [SETTINGS_ROUTES.fxRates]: 'settings.fx.section.rates',
  [SETTINGS_ROUTES.email]: 'settings.rail.email',
  [SETTINGS_ROUTES.emailTemplates]: 'settings.rail.emailTemplates',
  [SETTINGS_ROUTES.companies]: 'settings.rail.companies',
  [SETTINGS_ROUTES.documentNumbering]: 'settings.rail.documentNumbering',
  [SETTINGS_ROUTES.expenseCategories]: 'settings.rail.expenseCategories',
  [SETTINGS_ROUTES.apps]: 'settings.rail.apps',
  [SETTINGS_ROUTES.integrations.marketplaces]: 'settings.rail.marketplaces',
  [SETTINGS_ROUTES.integrations.efatura]: 'settings.rail.efatura',
  [SETTINGS_ROUTES.integrations.efaturaLogs]: 'nav.efaturaLogs',
  [SETTINGS_ROUTES.security.users]: 'settings.rail.users',
  [SETTINGS_ROUTES.security.auditLogs]: 'settings.rail.auditLogs',
  [SETTINGS_ROUTES.security.permissions]: 'settings.rail.permissions',
  [SETTINGS_ROUTES.billing]: 'settings.rail.billing',
};

/** Son segmenti adlandıramadığımızda kullanılan jenerik etiketler. */
const TRAILING_SEGMENT_LABELS: Readonly<Record<string, string>> = {
  new: 'common.breadcrumb.new',
  edit: 'common.breadcrumb.edit',
  print: 'common.breadcrumb.print',
  logs: 'nav.efaturaLogs',
};

export interface BreadcrumbEntry {
  /** i18n anahtarı; `rawLabel` doluysa yok sayılır. */
  labelKey?: string;
  /** Doğrudan gösterilecek metin (dinamik kayıt kimlikleri için). */
  rawLabel?: string;
  /** Tıklanabilir olduğunda hedef yol; son (aktif) girdide undefined. */
  path?: string;
}

/**
 * Bir pathname'i breadcrumb zincirine çevirir.
 *
 * `/dashboard` her zaman ilk halkadır; ondan sonraki her tanınan prefix bir
 * halka ekler. Tanınmayan son segment, sabit bir etiketi varsa onunla, yoksa
 * jenerik "Detay" ile temsil edilir — böylece `/products/<uuid>` sessizce
 * "Ürünler" olarak bitip kullanıcıyı bir üst seviyede olduğu yanılgısına
 * düşürmez.
 */
export function buildBreadcrumb(pathname: string): BreadcrumbEntry[] {
  const clean = pathname.replace(/\/+$/, '') || '/dashboard';
  const segments = clean.split('/').filter(Boolean);

  const entries: BreadcrumbEntry[] = [];
  let acc = '';
  let lastMatchedIndex = -1;

  segments.forEach((segment, index) => {
    acc += `/${segment}`;
    const labelKey = ROUTE_LABELS[acc];
    if (labelKey) {
      entries.push({ labelKey, path: acc });
      lastMatchedIndex = index;
    }
  });

  // Eşleşmeyen kuyruk: yalnız SON segment için bir halka ekle. Aradaki
  // adlandırılamayan segmentler (örn. `integrations`) gezinilebilir sayfa
  // olmadıkları için atlanır.
  const trailing = segments[segments.length - 1];
  if (trailing !== undefined && lastMatchedIndex < segments.length - 1) {
    const trailingKey = TRAILING_SEGMENT_LABELS[trailing];
    entries.push(
      trailingKey ? { labelKey: trailingKey } : { labelKey: 'common.breadcrumb.detail' },
    );
  }

  const last = entries[entries.length - 1];
  if (last) delete last.path;
  return entries;
}
